import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram } from '@if/core/types'
import { Alert, Box, Divider } from '@mui/material'
import { memo, useEffect, useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ActionsDialog, PaperProgress } from '../../../components'
import { ApplicantIncomes, applicantIncomesSchema } from '../../../data/types'
import { reportErrorToConsole } from '../../../services/error-logger'
import IncomeForm from './IncomeForm'
import OtherIncomeForm from './OtherIncomeForm'

interface Props {
  open: boolean
  title: string
  defaultValue: ApplicantIncomes | undefined
  onConfirm: (data: ApplicantIncomes) => void
  onCancel: () => void
  disableConfirm?: boolean
}

const EditIncomesDialog = ({ defaultValue, onConfirm, onCancel, open, title, disableConfirm }: Props) => {
  const { t } = useTranslation()

  const safeDefaultValues = useMemo(
    () => ({
      currentJobs: defaultValue?.currentJobs ?? [],
      otherIncomes: defaultValue?.otherIncomes ?? [],
      financingProgramId: defaultValue?.financingProgramId ?? EFinancingProgram.Auto,
    }),
    [defaultValue],
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: safeDefaultValues, // <-- ensure this is present
    resolver: yupResolver(applicantIncomesSchema),
  })

  useEffect(() => {
    reset(safeDefaultValues)
  }, [safeDefaultValues, reset])

  const { fields: otherIncomesFields, remove: otherIncomesRemove } = useFieldArray({
    name: 'otherIncomes',
    control,
  })

  const { fields: currentJobsFields, remove: currentJobsRemove } = useFieldArray({
    name: 'currentJobs',
    control,
  })

  return (
    <ActionsDialog
      title={title}
      open={open}
      onCancel={onCancel}
      onConfirm={handleSubmit(onConfirm, reportErrorToConsole)}
      disabled={disableConfirm}
    >
      <Box sx={{ position: 'relative' }}>
        <Box mt={2} />
        {errors.currentJobs?.root?.message && (
          <Alert severity="error" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem', textAlign: 'center' }}>
            {errors.currentJobs.root.message}
          </Alert>
        )}
        {currentJobsFields.length !== 0 && (
          <Divider textAlign="left" sx={{ marginTop: 1 }}>
            {t('editCreditApplication.income.job.label')}
          </Divider>
        )}
        {currentJobsFields.map((job, index) => (
          <IncomeForm
            index={index}
            register={register}
            error={errors?.currentJobs}
            key={job.id}
            onDelete={() => currentJobsRemove(index)}
          />
        ))}
        {otherIncomesFields.length !== 0 && (
          <Divider textAlign="left" sx={{ marginTop: 1 }}>
            {t('editCreditApplication.income.other.label')}
          </Divider>
        )}
        {otherIncomesFields.map((otherIncome, index) => (
          <OtherIncomeForm
            index={index}
            register={register}
            formControl={control}
            error={errors?.otherIncomes}
            key={otherIncome.id}
            onDelete={() => otherIncomesRemove(index)}
          />
        ))}
        <PaperProgress isLoading={disableConfirm ?? false} />
      </Box>
    </ActionsDialog>
  )
}

export default memo(EditIncomesDialog)
